import { FilterInfo } from '@/app/shared/api/endpoints/SearchInvoices';
import { ButtonFilterSelection } from './invoiceTypes';
import {
  DEFAULT_DATE_FILTER,
  DEFAULT_EXCLUDED_STATUSES,
  DEFAULT_SEARCH_FIELDS,
  FILTER_FIELDS,
} from '@/app/features/DebtorPortal/Dashboard/dashboard.types';

/**
 * Creates a date range filter for invoice dates
 */
export const createDateRangeFilter = (
  startDate: Date,
  endDate: Date,
  field: string = FILTER_FIELDS.invoiceDate,
): FilterInfo => ({
  condition: 'and',
  filter: [
    {
      field,
      filterType: 'gte',
      value: startDate.toISOString(),
    },
    {
      field,
      filterType: 'lte',
      value: endDate.toISOString(),
    },
  ],
});

/**
 * Creates a search term filter that searches across multiple fields
 */
export const createSearchFilter = (
  searchTerm: string,
  fields: readonly string[] = DEFAULT_SEARCH_FIELDS,
): FilterInfo => ({
  condition: 'or',
  filter: fields.map((field) => ({
    field,
    filterType: 'contains',
    value: searchTerm.trim(),
  })),
});

/**
 * Creates filters from button filter selections
 */
export const createButtonFilters = (
  buttonFilters: ButtonFilterSelection[],
): FilterInfo[] => {
  if (buttonFilters.length === 0) {
    return [];
  }

  const filtersByColumn: Record<string, string[]> = {};
  buttonFilters.forEach((filter) => {
    if (!filtersByColumn[filter.column]) {
      filtersByColumn[filter.column] = [];
    }
    filtersByColumn[filter.column].push(filter.value);
  });

  const filters: FilterInfo[] = [];
  Object.entries(filtersByColumn).forEach(([column, values]) => {
    if (values.length === 1) {
      filters.push({
        field: column,
        filterType: 'eq',
        value: values[0],
      });
    } else {
      filters.push({
        condition: 'or',
        filter: values.map((value) => ({
          field: column,
          filterType: 'eq',
          value: value,
        })),
      });
    }
  });

  return filters;
};

/**
 * Creates a due date filter for dates greater than a specific date
 */
export const createDueDateGreaterThanFilter = (date: Date): FilterInfo => ({
  condition: 'and',
  filter: [
    {
      field: FILTER_FIELDS.dueDate,
      filterType: 'gt',
      value: date.toISOString().split('T')[0],
    },
  ],
});

/**
 * Creates a due date filter for dates less than a specific date
 */
export const createDueDateLessThanFilter = (date: Date): FilterInfo => ({
  condition: 'and',
  filter: [
    {
      field: FILTER_FIELDS.dueDate,
      filterType: 'lt',
      value: date.toISOString().split('T')[0],
    },
  ],
});

/**
 * Creates a filter to exclude a specific status value
 */
export const createStatusNotEqualFilter = (statusValue: string): FilterInfo => ({
  field: FILTER_FIELDS.status,
  filterType: 'neq',
  value: statusValue,
});

/**
 * Creates the default status exclusion filters
 */
export const createDefaultStatusExclusionFilters = (): FilterInfo[] => {
  return DEFAULT_EXCLUDED_STATUSES.map((status) =>
    createStatusNotEqualFilter(status)
  );
};

/**
 * Combines multiple filters into a single AND filter
 */
export const combineFilters = (
  filters: (FilterInfo | undefined)[],
): FilterInfo | undefined => {
  const validFilters = filters.filter((f): f is FilterInfo => f !== undefined);

  if (validFilters.length === 0) {
    return undefined;
  }

  if (validFilters.length === 1) {
    return validFilters[0];
  }

  return {
    condition: 'and',
    filter: validFilters,
  };
};

/**
 * Gets the default date filter
 */
export const getDefaultDateFilter = (): FilterInfo => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - DEFAULT_DATE_FILTER.filterMonth);
  return createDateRangeFilter(startDate, endDate);
};
